import Vue from 'vue';
import moment from "moment";
import Util from '@/helper/Utility.js';
import Locale from '@/helper/locale/locale.js';
import BzbsAuth from "@/service/BzbsAuth.js";
import BzbsProfile from '@/service/BzbsProfile.js';
import { ProfileModel, ProfileAddressModel, ProfileShippingAddressModel } from '@/model/AccountModel.js';

//Main key.
const KEY_COOKIE_USER = 'BzbsUser';
const KEY_COOKIE_PROFILE = 'BzbsProfile';
const KEY_COOKIE_PROFILE_ADDRESS = 'BzbsProfileAddress';
const KEY_COOKIE_PROFILE_SHIPPING_ADDRESS = 'BzbsProfileShippingAddress';

const KEY_COOKIE_USER_POPUP = 'BzbsUserPopup';
const KEY_COOKIE_PROFILE_POPUP = 'BzbsProfilePopup';
const KEY_COOKIE_PROFILE_ADDRESS_POPUP = 'BzbsProfileAddressPopup';
const KEY_COOKIE_PROFILE_SHIPPING_ADDRESS_POPUP = 'BzbsProfileShippingAddressPopup';

/**
 * Token
 * @method getToken
 * @method getShuffleToken
 * @method updateToken
 *
 * Cache
 * @method getCacheUser
 * @method setCacheUser
 * @method removeCacheUser
 * @method getCachePoint
 * @method updatePoint
 * @method getCacheCustom
 * @method setCacheCustom
 * @method removeCacheCustom
 *
 * Login
 * @method setLogin
 * @method setLogout
 * @method loginStatus
 * @method isLogin
 *
 * Api login
 * @method apiGetPoint
 * @method apiBzbsLogin
 * @method apiDeviceLogin
 * @method apiFBLogin
 * @method apiLogout
 *
 * Cache profile
 * @method getCacheProfile
 * @method setCacheProfile
 * @method removeCacheProfile
 * @method getCacheProfileAddress
 * @method setCacheProfileAddress
 * @method removeCacheProfileAddress
 * @method getCacheProfileShippingAddres
 * @method setCacheProfileShippingAddres
 * @method removeCacheProfileShippingAddres
 * @method setCacheAllProfile
 * @method removeCacheAllProfile
 *
 * Api profile
 * @method apiGetProfile
 * @method apiUpdateProfile
 * @method apiUpdateProfileLocale
 * @method apiUpdateProfileShippingAddress
 */
export default {
    initial: function () {
        Vue.initFinished = false;
        this.apiGetProfile().then(() => {
            Vue.initFinished = true;
        }).catch(() => {
            Vue.initFinished = true;
        });
    },
    //#region user
    /**
     * Get user token.
     * @return {string|null} user token.
     */
    getToken: function () {
        return this.getCacheUser() ? this.getCacheUser().token : null;
    },

    getShuffleToken: function() {
        return Util.shuffleToken(this.getToken());
    },

    /**
     * Update user token.
     * @param {Object} strToken
     * @return {object} user.
     */
    updateToken: function (strToken) {
        var objUser = this.getCacheUser();
        objUser.token = strToken;
        this.setCacheUser(objUser);

        return objUser;
    },

    /**
     * Get cache user.
     * @return {object} Cache user.
     */
    getCacheUser: function () {
        var isPopup = this.isPopup();
        if (isPopup == "true") {
            return Vue.$cookies.get(KEY_COOKIE_USER_POPUP);
        } else {
            return Vue.$cookies.get(KEY_COOKIE_USER);
        }

    },

    /**
     * Set cache user.
     * @param {object} objUser
     */
    setCacheUser: function (objUser) {
        this.removeCacheUser();
        var isPopup = this.isPopup();
        if (isPopup == "true") {
            Vue.$cookies.set(KEY_COOKIE_USER_POPUP, JSON.stringify(objUser));
        }else {
            Vue.$cookies.set(KEY_COOKIE_USER, JSON.stringify(objUser));
        }

    },

    /**
     * Get point.
     * @return {number} User point.
     */
    getCachePoint: function () {
        if (this.isLogin()) {
            var objUser = this.getCacheUser();
            if (objUser.updated_points != null &&
                objUser.updated_points.points != null) {
                return objUser.updated_points.points;
            }
        }

        return 0;
    },

    /**
     * Remove cache user.
     */
    removeCacheUser: function () {
        var isPopup = this.isPopup();
        if (isPopup == "true") {
            Vue.$cookies.remove(KEY_COOKIE_USER_POPUP);
        }else {
            Vue.$cookies.remove(KEY_COOKIE_USER);
        }

    },

    /**
     * Set user login.
     * @param {object} objUser
     */
    setLogin: function (objUser) {
        this.setCacheUser(objUser);
        var localeCode = objUser.locale;
        Locale.setLocale(localeCode);
    },

    /**
     * Set user logout.
     * @return {promises} Logout response.
     */
    setLogout: function () {
        this.removeCacheUser();
        this.removeCacheAllProfile();

        return this.apiLogout();
    },

    /**
     * Update point.
     * @param {object} objUpdatePoints
     */
    updatePoint: function (objUpdatePoints) {
        var objUser = this.getCacheUser();
        var objProfile = this.getCacheProfile();
        objUser.updated_points = objUpdatePoints;
        objProfile.updated_points = objUpdatePoints;
        this.setCacheUser(objUser);
        this.setCacheProfile(objProfile);
    },


    /**
     * Check login wait for check token status.
     * @return Promise
     */
    loginStatus: function () {
        return new Promise((resolve) => {
            if (Vue.initFinished) {
                var strToken = this.getToken();
                var rs = !Util.isEmptyStr(strToken);
                resolve(rs);
            } else {
                var itv = setInterval(() => {
                    if (Vue.initFinished) {
                        var strToken = this.getToken();
                        var rs = !Util.isEmptyStr(strToken);
                        clearInterval(itv);
                        resolve(rs)
                    }
                }, 200);
            }
        });
    },

    /**
     * Check login.
     * @return {boolean} Login status.
     */
    isLogin: function() {
        var strToken = this.getToken();
        return !Util.isEmptyStr(strToken);
    },

    /**
     * Get user point.
     * @returns {promises} Update point object.
     */
    apiGetPoint: function () {
        return new Promise((resolve, reject) => {
            var strToken = this.getToken();
            BzbsProfile.getPoint(strToken)
                .then((response) => {
                    this.updatePoint(response.data);
                    resolve(response.data);
                }).catch((error) => {
                    reject(error);
                });
        });
    },

    /**
     * Bzbs login.
     * @param {string} strUsername
     * @param {string} strPassword
     * @return {object} Return user and profile.
     */
    apiBzbsLogin: function (strUsername, strPassword) {
        return new Promise((resolve, reject) => {
            BzbsAuth.apiLogin(strUsername, strPassword, null, null, null, false).then((response) => {
                //Set User to cache.
                this.setLogin(response.data);
                //Get Profile to cache.
                this.apiGetProfile().then((objProfile) => {
                    //Return User Data from api.
                    var objResult = {
                        "user": response.data,
                        "profile": objProfile
                    };
                    resolve(objResult);
                }).catch((error) => {
                    reject(error);
                });

            }).catch((error) => {
                reject(error);
            });
        });
    },

    apiDeviceLogin: function () {

    },

    apiFBLogin: function () {

    },

    /**
     * Logout.
     */
    apiLogout: function () {
        return new Promise((resolve, reject) => {
            var strToken = this.getToken();
            if (!strToken) {
                resolve(null);
            } else {
                BzbsAuth.apiLogout(strToken, null, null).then((response) => {
                    resolve(response);
                }).catch((error) => {
                    reject(error);
                });
            }
        });
    },
    //#endregion user

    //#region profile
    /**
     * Get cache profile.
     * @return {object|null} Profile.
     */
    getCacheProfile: function () {
        var isPopup = this.isPopup();
        if (isPopup == "true") {
            return Vue.$cookies.get(KEY_COOKIE_PROFILE_POPUP);
        }else {
            return Vue.$cookies.get(KEY_COOKIE_PROFILE);
        }

    },

    /**
     * Set cache profile.
     * @param {object|null} objProfile
     */
    setCacheProfile: function (objProfile) {
        this.removeCacheProfile();

        var isPopup = this.isPopup();
        if (isPopup == "true") {
            Vue.$cookies.set(KEY_COOKIE_PROFILE_POPUP, JSON.stringify(objProfile));
        } else {
            Vue.$cookies.set(KEY_COOKIE_PROFILE, JSON.stringify(objProfile));
        }
    },

    /**
     * Remove cache profile.
     */
    removeCacheProfile: function () {

        var isPopup = this.isPopup();
        if (isPopup == "true") {
            Vue.$cookies.remove(KEY_COOKIE_PROFILE_POPUP);
        } else {
            Vue.$cookies.remove(KEY_COOKIE_PROFILE);
        }
    },

    /**
     * Get cache profile address.
     * @return {object|null}
     */
    getCacheProfileAddress: function () {

        var isPopup = this.isPopup();
        if (isPopup == "true") {
            return Vue.$cookies.get(KEY_COOKIE_PROFILE_ADDRESS_POPUP);
        } else {
            return Vue.$cookies.get(KEY_COOKIE_PROFILE_ADDRESS);
        }
    },

    /**
     * Set cache profile address.
     * @param {object|null} objProfileAddress
     */
    setCacheProfileAddress: function (objProfileAddress) {
        this.removeCacheProfileAddress();

        var isPopup = this.isPopup();
        if (isPopup == "true") {
            Vue.$cookies.set(KEY_COOKIE_PROFILE_ADDRESS_POPUP, JSON.stringify(objProfileAddress));
        } else {
            Vue.$cookies.set(KEY_COOKIE_PROFILE_ADDRESS, JSON.stringify(objProfileAddress));
        }
    },

    /**
     * Remove cache profile address.
     */
    removeCacheProfileAddress: function () {

        var isPopup = this.isPopup();
        if (isPopup == "true") {
            Vue.$cookies.remove(KEY_COOKIE_PROFILE_ADDRESS_POPUP);
        } else {
            Vue.$cookies.remove(KEY_COOKIE_PROFILE_ADDRESS);
        }
    },

    /**
     * Get cache profile shipping address.
     * @return {object|null} Profile shipping address
     */
    getCacheProfileShippingAddres: function () {

        var isPopup = this.isPopup();
        if (isPopup == "true") {
            return Vue.$cookies.get(KEY_COOKIE_PROFILE_SHIPPING_ADDRESS_POPUP);
        } else {
            return Vue.$cookies.get(KEY_COOKIE_PROFILE_SHIPPING_ADDRESS);
        }
    },

    /**
     * Set cache profile shipping address.
     * @param {object|null} objProfileShippingAddress
     */
    setCacheProfileShippingAddres: function (objProfileShippingAddress) {
        this.removeCacheProfileShippingAddres();

        var isPopup = this.isPopup();
        if (isPopup == "true") {
            Vue.$cookies.set(KEY_COOKIE_PROFILE_SHIPPING_ADDRESS_POPUP, JSON.stringify(objProfileShippingAddress));
        } else {
            Vue.$cookies.set(KEY_COOKIE_PROFILE_SHIPPING_ADDRESS, JSON.stringify(objProfileShippingAddress));
        }
    },
    /**
     * Remove cache profile shipping address.
     */
    removeCacheProfileShippingAddres: function () {

        var isPopup = this.isPopup();
        if (isPopup == "true") {
            Vue.$cookies.remove(KEY_COOKIE_PROFILE_SHIPPING_ADDRESS_POPUP);
        } else {
            Vue.$cookies.remove(KEY_COOKIE_PROFILE_SHIPPING_ADDRESS);
        }
    },

    /**
     * Set cache for profile, profile address, profile shipping address.
     * @param {object|null} objProfile
     * @param {object|null} objProfileAddress
     * @param {object|null} objProfileShippingAddress
     */
    setCacheAllProfile: function (objProfile, objProfileAddress, objProfileShippingAddress) {
        this.removeCacheAllProfile();
        this.setCacheProfile(objProfile);
        this.setCacheProfileAddress(objProfileAddress);
        this.setCacheProfileShippingAddres(objProfileShippingAddress);
    },

    /**
     * Remove all profile cache.
     */
    removeCacheAllProfile: function () {
        this.removeCacheProfile();
        this.removeCacheProfileAddress();
        this.removeCacheProfileShippingAddres();
    },

    /**
     * Get profile.
     * @return {object|null} Profile.
     */
    apiGetProfile: function () {
        return new Promise((resolve, reject) => {
            var strToken = this.getToken();
            if (!strToken) {
                resolve(null);
            } else {
                BzbsProfile.getProfile(strToken).then((response) => {
                    var objProfile = new ProfileModel(response.data);
                    var objProfileAddress = new ProfileAddressModel(response.data);
                    var objProfileShippingAddress = new ProfileShippingAddressModel(response.data);
                    this.setCacheAllProfile(objProfile, objProfileAddress, objProfileShippingAddress);
                    resolve(objProfile);
                }).catch((error) => {
                    if (error.response.data.error.code == 412) {
                        this.removeCacheUser();
                        this.removeCacheAllProfile();
                        location.reload();
                        console.log('error token');
                    }
                    reject(error);
                });
            }
        });
    },

    /**
     * Update user profile.
     * @param {object} objProfile
     * @param {object} objProfileAddress
     * @param {string} strExtendJson
     */
    apiUpdateProfile: function (objProfile, objProfileAddress, strExtendJson) {
        return new Promise((resolve, reject) => {
            var strToken = this.getToken();
            if (!strToken) {
                resolve(null);
            } else {
                BzbsProfile.updateProfile(strToken, objProfile, objProfileAddress, strExtendJson).then((response) => {
                    //Update cache profile.
                    var objNewProfile = new ProfileModel(response.data);
                    var objNewProfileAddress = new ProfileAddressModel(response.data);
                    var objNewProfileShippingAddress = new ProfileShippingAddressModel(response.data);
                    this.setCacheAllProfile(objNewProfile, objNewProfileAddress, objNewProfileShippingAddress);
                    //Update cache user.
                    var objUser = this.getCacheUser();
                    objUser.token = response.data.Token;
                    objUser.updated_points = objNewProfile.updated_points;
                    this.setCacheUser(objUser);

                    var objResult = {
                        "objUser": objUser,
                        "objProfile": objProfile
                    };
                    resolve(objResult);
                }).catch((error) => {
                    reject(error);
                });
            }
        });
    },

    /**
     * Update user locale.
     * @param {number} localeCode
     */
    apiUpdateProfileLocale: function (localeCode) {
        var objProfile = this.getCacheProfile();
        if (objProfile)
            objProfile.locale = localeCode;

        var objProfileAddress = this.getCacheProfileAddress();

        return this.apiUpdateProfile(objProfile, objProfileAddress);
    },

    /**
     * Update user shipping address.
     * @param {object} objProfileShippingAddress
     */
    apiUpdateProfileShippingAddress: function (objProfileShippingAddress) {
        return new Promise((resolve, reject) => {
            var strToken = this.getToken();
            if (!strToken) {
                resolve(null);
            } else {
                BzbsProfile.updateShippingAddress(objProfileShippingAddress)
                    .then((objResult) => {
                        var objNewProfileShippingAddress = new ProfileShippingAddressModel(objResult);
                        this.setCacheProfileShippingAddres(objNewProfileShippingAddress);
                        resolve(objNewProfileShippingAddress);
                    }).catch((error) => {
                        reject(error);
                    });
            }
        });
    },
    //#endregion profile

    //#region general function
    /**
     * Get cache json by key.
     * @param {string} strCookieKey
     * @return {object|null} Profile extend json.
     */
    getCacheCustom: function (strCookieKey) {
        return Vue.$cookies.get(strCookieKey);
    },

    /**
     * Set cache json by key.
     * @param {string} strCookieKey
     * @param {object|null} objData
     */
    setCacheCustom: function (strCookieKey, objData) {
        this.removeCacheCustom(strCookieKey);
        Vue.$cookies.set(strCookieKey, JSON.stringify(objData));
    },

    /**
     * Remove cache extend json.
     * @param {string} strCookieKey
     */
    removeCacheCustom: function (strCookieKey) {
        Vue.$cookies.remove(strCookieKey);
    },
    //#endregion general function
    /**
     * Get Program Tier Thailife.
     * @param {string} userlevel
     */
    getProgramTier: function (userlevel) {
        var program = "";
        switch (userlevel) {
            case "PRIME":
                program = "PRIME";
                break;
            case "CLASSIC":
            case "SILVER":
            case "GOLD":
            case "PLATINUM":
            case "MINUS":
                program = "INFINITE";
                break;
            case "ULTIMATE":
                program = "ULTIMATE";
                break;
            case "REGULAR":
                program = "REGULAR";
                break;
        }

        return program;
    },
    /**
     * Get Program Tier Thailife.
     * @param {string} userlevel
     */
    getTrackViewGTM: function (user) {
        var gtm = {};
        var customer_id = "anonymous";
        var infinite_program = "anonymous";
        var infinite_tier = "anonymous"
        var platform = Util.getMobileOperatingSystem();

        this.consent = this.getCacheCustom('consent');

        if (this.consent == "true") {
            customer_id = user.userId.replace("THL_","");
            infinite_tier = user.detail.level;
            infinite_program = this.getProgramTier(user.detail.level)
        }
        gtm = {
            "pagePath": encodeURIComponent(window.location.href),
            "customer_id": customer_id,
            "infinite_program": infinite_program,
            "infinite_tier": infinite_tier,
            "platform": platform,
            "timestamp": moment().format("YYYYMMDD HH:mm:ss")
        };

        return gtm;
    },

    isPopup: function() {
        var name = "popup";
        var url = window.location.href
        var regex = new RegExp('[?&]' + name + '(=([^&#]*)|&|#|$)'),
            results = regex.exec(url);
        if (!results) return null;
        if (!results[2]) return '';
        return decodeURIComponent(results[2].replace(/\+/g, ' '));
    }
}
